import { useState, useEffect } from 'react';
import { Plus, X, Save, Trash2, ArrowLeft, Crosshair, Loader2, Clock, CalendarPlus, Globe, UserPlus, User } from 'lucide-react';
import { showToast } from './Toast';
import { formatPhoneInput, formatPhoneUS, formatZipInput } from '../utils/phone';

const LANGUAGE_OPTIONS = [
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'Inglês' },
  { value: 'es', label: 'Espanhol' },
  { value: 'ar', label: 'Árabe' },
  { value: 'zh', label: 'Chinês' },
  { value: 'other', label: 'Outro' }
];

// Check if Contact Picker API is available
const isContactPickerSupported = () => {
  return 'contacts' in navigator && 'ContactsManager' in window;
};

// Capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export function NovaLojaForm({ loja, onSave, onDelete, onCancel, formType = 'visitada' }) {
  const today = new Date().toISOString().split('T')[0];
  const isAVisitar = formType === 'a_visitar' || loja?.status === 'a_visitar';
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: 'MA',
    zip: '',
    visit: isAVisitar ? '' : today,
    manager: '',
    language: 'pt',
    notes: '',
    sellers: [],
    visitHistory: [],
    status: formType
  });

  const [newSeller, setNewSeller] = useState({ name: '', phone: '', photo: null });
  const [newVisitNote, setNewVisitNote] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [showPhotoInput, setShowPhotoInput] = useState(false);
  const isEditing = !!loja;

  useEffect(() => {
    if (loja) {
      setFormData({
        name: loja.name || '',
        address: loja.address || '',
        city: loja.city || '',
        state: loja.state || 'MA',
        zip: loja.zip || '',
        visit: loja.visit || (isAVisitar ? '' : today),
        manager: loja.manager || '',
        language: loja.language || 'pt',
        notes: loja.notes || '',
        sellers: loja.sellers || [],
        visitHistory: loja.visitHistory || [],
        status: loja.status || formType
      });
    }
  }, [loja, today, isAVisitar, formType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else if (name === 'name' || name === 'city' || name === 'manager') {
      // Capitalize first letter of each word for names
      setFormData(prev => ({ ...prev, [name]: capitalizeWords(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCaptureGPS = () => {
    if (!navigator.geolocation) {
      showToast('GPS não disponível neste dispositivo', 'error');
      return;
    }

    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          deviceLat: position.coords.latitude,
          deviceLng: position.coords.longitude
        }));
        showToast(`GPS capturado: ${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`);
        setGpsLoading(false);
      },
      (error) => {
        let message = 'Erro ao obter localização';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permissão de localização negada';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Localização indisponível';
            break;
          case error.TIMEOUT:
            message = 'Tempo limite excedido';
            break;
          default:
            break;
        }
        showToast(message, 'error');
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleAddSeller = () => {
    if (!newSeller.name.trim()) {
      showToast('Digite o nome do vendedor', 'warning');
      return;
    }
    const formattedPhone = formatPhoneUS(newSeller.phone);
    setFormData(prev => ({
      ...prev,
      sellers: [...prev.sellers, { 
        id: `seller_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: newSeller.name, 
        phone: formattedPhone,
        photo: newSeller.photo || null
      }]
    }));
    setNewSeller({ name: '', phone: '', photo: null });
    setShowPhotoInput(false);
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneInput(value);
    setNewSeller(prev => ({ ...prev, phone: formatted }));
  };

  const handleRemoveSeller = (sellerId) => {
    setFormData(prev => ({
      ...prev,
      sellers: prev.sellers.filter((s) => s.id !== sellerId)
    }));
  };

  const handleAddVisit = () => {
    if (!newVisitNote.trim()) {
      showToast('Digite uma nota da visita', 'warning');
      return;
    }
    const newVisit = {
      id: `visit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      date: today,
      note: newVisitNote.trim()
    };
    setFormData(prev => ({
      ...prev,
      visit: today,
      visitHistory: [newVisit, ...(prev.visitHistory || [])]
    }));
    setNewVisitNote('');
    showToast('Visita registrada!');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Nome da loja é obrigatório', 'error');
      return;
    }
    if (!formData.city.trim()) {
      showToast('Cidade é obrigatória', 'error');
      return;
    }

    const lojaData = {
      ...formData,
      id: loja?.id || `s${Date.now()}`,
      lat: formData.deviceLat || loja?.lat || null,
      lng: formData.deviceLng || loja?.lng || null
    };

    delete lojaData.deviceLat;
    delete lojaData.deviceLng;

    onSave(lojaData);
  };

  const handleDelete = () => {
    if (window.confirm(`Tem certeza que deseja excluir "${formData.name}"?`)) {
      onDelete(loja.id);
    }
  };

  // Import contact from phone
  const handleImportContact = async () => {
    if (!isContactPickerSupported()) {
      // Show manual photo input for desktop
      setShowPhotoInput(true);
      showToast('Em desktop, adicione a foto manualmente abaixo', 'info');
      return;
    }

    setContactLoading(true);
    
    try {
      const props = ['name', 'tel'];
      const opts = { multiple: false };
      
      // Check if icon (photo) is supported
      const supportedProps = await navigator.contacts.getProperties();
      if (supportedProps.includes('icon')) {
        props.push('icon');
      }
      
      const contacts = await navigator.contacts.select(props, opts);
      
      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        const name = contact.name?.[0] || '';
        const phone = contact.tel?.[0] || '';
        let photoUrl = null;
        
        // Convert photo blob to data URL
        if (contact.icon && contact.icon.length > 0) {
          const blob = contact.icon[0];
          photoUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        }
        
        setNewSeller({
          name: name,
          phone: formatPhoneInput(phone.replace(/\D/g, '')),
          photo: photoUrl
        });
        
        showToast(`Contato "${name}" importado!`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error importing contact:', error);
        showToast('Erro ao importar contato', 'error');
      }
    } finally {
      setContactLoading(false);
    }
  };

  // Handle photo file input for desktop
  const handlePhotoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showToast('Foto muito grande. Máximo 2MB', 'error');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewSeller(prev => ({ ...prev, photo: reader.result }));
        showToast('Foto adicionada!');
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  const inputClass = "w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] transition-all placeholder:text-[var(--text-muted)]";
  const labelClass = "block text-sm font-medium text-[var(--text-secondary)] mb-1";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 pb-24">
      {/* Back Button */}
      <button
        type="button"
        data-testid="btn-back"
        onClick={onCancel}
        className={`flex items-center gap-2 ${isAVisitar ? 'text-amber-500' : 'text-[var(--accent-primary)]'} text-sm font-medium mb-2`}
      >
        <ArrowLeft size={18} /> Voltar
      </button>

      <h2 className="text-xl font-bold text-[var(--text-primary)]">
        {isEditing 
          ? (isAVisitar ? 'Editar Loja a Visitar' : 'Editar Loja') 
          : (isAVisitar ? 'Nova Loja a Visitar' : 'Nova Loja')
        }
      </h2>

      {/* Status Badge */}
      {isAVisitar && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <span className="text-amber-500 text-sm font-medium">📍 Esta loja será adicionada à lista &quot;A Visitar&quot;</span>
        </div>
      )}

      {/* Name */}
      <div>
        <label className={labelClass}>Store Name *</label>
        <input
          data-testid="input-nome"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Ex: Boston Auto Center"
          className={inputClass}
        />
      </div>

      {/* Address */}
      <div>
        <label className={labelClass}>Address</label>
        <input
          data-testid="input-endereco"
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Ex: 123 Main Street"
          className={inputClass}
        />
      </div>

      {/* City + State */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>City *</label>
          <input
            data-testid="input-cidade"
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="Ex: Boston"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>State</label>
          <input
            data-testid="input-estado"
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            maxLength={2}
            placeholder="MA"
            className={`${inputClass} uppercase`}
          />
        </div>
      </div>

      {/* ZIP + Visit Date */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>ZIP Code</label>
          <input
            data-testid="input-zip"
            type="text"
            name="zip"
            value={formData.zip}
            onChange={(e) => {
              const formatted = formatZipInput(e.target.value);
              setFormData(prev => ({ ...prev, zip: formatted }));
            }}
            placeholder="02128"
            maxLength={10}
            className={inputClass}
          />
        </div>
        {!isAVisitar && (
          <div>
            <label className={labelClass}>Last Visit</label>
            <input
              data-testid="input-visita"
              type="date"
              name="visit"
              value={formData.visit}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
        )}
      </div>

      {/* GPS Capture Button */}
      <div>
        <label className={labelClass}>GPS Location</label>
        <button
          type="button"
          data-testid="btn-capture-gps"
          onClick={handleCaptureGPS}
          disabled={gpsLoading}
          className="w-full h-11 bg-purple-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-purple-700 font-medium text-sm disabled:opacity-50"
        >
          {gpsLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Obtendo localização...
            </>
          ) : (
            <>
              <Crosshair size={18} /> Capturar Localização Atual
            </>
          )}
        </button>
        {(formData.deviceLat || (loja?.lat && loja?.lng)) && (
          <p className="text-xs text-green-500 mt-1 text-center">
            📍 {formData.deviceLat?.toFixed(4) || loja?.lat?.toFixed(4)}, {formData.deviceLng?.toFixed(4) || loja?.lng?.toFixed(4)}
          </p>
        )}
      </div>

      {/* Manager */}
      <div>
        <label className={labelClass}>Manager</label>
        <input
          data-testid="input-gerente"
          type="text"
          name="manager"
          value={formData.manager}
          onChange={handleChange}
          placeholder="Ex: John Smith"
          className={inputClass}
        />
      </div>

      {/* Language */}
      <div>
        <label className={labelClass}>
          <span className="flex items-center gap-2">
            <Globe size={14} className="text-[var(--text-muted)]" />
            Store Language
          </span>
        </label>
        <select
          data-testid="select-language"
          name="language"
          value={formData.language}
          onChange={handleChange}
          className={inputClass}
        >
          {LANGUAGE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          data-testid="input-observacoes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Store notes..."
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* Sellers Section */}
      <div className="bg-[var(--bg-chip)] rounded-lg p-4 border border-[var(--border-color)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Sellers</h3>
          <button
            type="button"
            data-testid="btn-import-contact"
            onClick={handleImportContact}
            disabled={contactLoading}
            className="flex items-center gap-1 text-xs text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 px-2 py-1 rounded-full hover:bg-[var(--accent-primary)]/20 transition-colors disabled:opacity-50"
          >
            {contactLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <UserPlus size={12} />
            )}
            {isContactPickerSupported() ? 'Importar Contato' : 'Adicionar Foto'}
          </button>
        </div>
        
        {/* Add Seller Form */}
        <div className="flex flex-col gap-2 mb-3">
          {/* Photo Preview or Photo Input */}
          {newSeller.photo ? (
            <div className="flex items-center gap-3 p-2 bg-[var(--bg-card)] rounded-lg border border-[var(--border-color)]">
              <img 
                src={newSeller.photo} 
                alt="Foto do contato" 
                className="w-12 h-12 rounded-full object-cover border-2 border-[var(--accent-primary)]"
              />
              <div className="flex-1">
                <p className="text-xs text-[var(--text-muted)]">Foto do vendedor</p>
                <button
                  type="button"
                  onClick={() => setNewSeller(prev => ({ ...prev, photo: null }))}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remover foto
                </button>
              </div>
            </div>
          ) : showPhotoInput && (
            <div className="p-3 bg-[var(--bg-card)] rounded-lg border border-dashed border-[var(--border-color)]">
              <label className="flex flex-col items-center gap-2 cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-[var(--bg-chip)] border border-[var(--border-color)] flex items-center justify-center">
                  <User size={24} className="text-[var(--text-muted)]" />
                </div>
                <span className="text-xs text-[var(--accent-primary)]">Clique para adicionar foto</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoFileChange}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={() => setShowPhotoInput(false)}
                className="w-full text-xs text-[var(--text-muted)] mt-2 hover:underline"
              >
                Cancelar
              </button>
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              data-testid="input-seller-name"
              type="text"
              value={newSeller.name}
              onChange={(e) => setNewSeller(prev => ({ ...prev, name: capitalizeWords(e.target.value) }))}
              placeholder="Name"
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
            />
            <input
              data-testid="input-seller-phone"
              type="tel"
              value={newSeller.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="(617) 555-1234"
              maxLength={14}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
            />
            <button
              type="button"
              data-testid="btn-add-seller"
              onClick={handleAddSeller}
              className="p-2 bg-[var(--accent-primary)] text-white rounded-lg active:opacity-80 transition-colors"
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        {/* Sellers List */}
        {formData.sellers.length > 0 && (
          <div className="flex flex-col gap-2">
            {formData.sellers.map((seller) => (
              <div
                key={seller.id || `seller_legacy_${seller.name}`}
                data-testid={`seller-item-${seller.id || seller.name}`}
                className="flex items-center gap-3 bg-[var(--bg-card)] rounded-lg p-2 border border-[var(--border-color)]"
              >
                {/* Seller Photo */}
                {seller.photo ? (
                  <img 
                    src={seller.photo} 
                    alt={seller.name} 
                    className="w-10 h-10 rounded-full object-cover border border-[var(--border-color)]"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[var(--bg-chip)] border border-[var(--border-color)] flex items-center justify-center">
                    <User size={18} className="text-[var(--text-muted)]" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[var(--text-primary)] truncate">{seller.name}</p>
                  {seller.phone && (
                    <a 
                      href={`tel:${seller.phone.replace(/\D/g, '')}`}
                      className="text-xs text-[var(--accent-primary)] hover:underline"
                    >
                      {seller.phone}
                    </a>
                  )}
                </div>
                
                <button
                  type="button"
                  data-testid={`btn-remove-seller-${seller.id || seller.name}`}
                  onClick={() => handleRemoveSeller(seller.id)}
                  className="p-1 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visit History Section - Only for visited stores */}
      {isEditing && !isAVisitar && (
        <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-amber-500 flex items-center gap-2">
              <Clock size={16} /> Histórico de Visitas
            </h3>
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="text-xs text-amber-400 underline"
            >
              {showHistory ? 'Ocultar' : `Ver (${formData.visitHistory?.length || 0})`}
            </button>
          </div>
          
          {/* Add Visit Form */}
          <div className="flex gap-2 mb-3">
            <input
              data-testid="input-visit-note"
              type="text"
              value={newVisitNote}
              onChange={(e) => setNewVisitNote(e.target.value)}
              placeholder="Nota da visita de hoje..."
              className="flex-1 bg-[var(--bg-input)] border border-amber-500/30 rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-amber-500 placeholder:text-[var(--text-muted)]"
            />
            <button
              type="button"
              data-testid="btn-add-visit"
              onClick={handleAddVisit}
              className="p-2 bg-amber-600 text-white rounded-lg active:bg-amber-700 transition-colors"
            >
              <CalendarPlus size={18} />
            </button>
          </div>

          {/* Visit History List */}
          {showHistory && formData.visitHistory && formData.visitHistory.length > 0 && (
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
              {formData.visitHistory.map((visit) => (
                <div
                  key={visit.id || `visit_legacy_${visit.date}_${visit.note?.substring(0,10)}`}
                  data-testid={`visit-history-${visit.id || visit.date}`}
                  className="bg-[var(--bg-card)] rounded-lg p-3 border border-amber-500/20"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-amber-500 bg-amber-500/20 px-2 py-0.5 rounded">
                      {formatDate(visit.date)}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-primary)]">{visit.note}</p>
                </div>
              ))}
            </div>
          )}

          {showHistory && (!formData.visitHistory || formData.visitHistory.length === 0) && (
            <p className="text-xs text-amber-400 text-center py-2">Nenhuma visita registrada ainda</p>
          )}
        </div>
      )}

      {/* Save Button */}
      <button
        type="submit"
        data-testid="btn-salvar-loja"
        className={`w-full h-12 ${isAVisitar ? 'bg-amber-500 active:bg-amber-600' : 'bg-[var(--accent-primary)]'} text-white rounded-lg shadow-lg flex items-center justify-center gap-2 active:opacity-90 transition-transform active:scale-[0.98] font-medium mt-4`}
      >
        <Save size={18} /> {isAVisitar ? 'Salvar Loja a Visitar' : 'Salvar Loja'}
      </button>

      {/* Delete Button (only when editing) */}
      {isEditing && (
        <button
          type="button"
          data-testid="btn-excluir-loja"
          onClick={handleDelete}
          className="w-full h-12 bg-red-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-red-700 transition-transform active:scale-[0.98] font-medium"
        >
          <Trash2 size={18} /> Excluir Loja
        </button>
      )}
    </form>
  );
}
